import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { trackPurchase } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function BuchungErfolg() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", payment: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Keine Session-ID gefunden." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          const meta = data.metadata || {};
          const segment =
            meta.buyer_type === "pflegedienst" ||
            ["jahresplan-booking", "b2b-booking"].includes(meta.source)
              ? "b2b"
              : "b2c";
          trackPurchase((data.amount_total || 0) / 100, sessionId, segment);
          setState({ status: "paid", payment: data });
          return;
        }
        if (data.status === "complete") {
          // Session abgeschlossen, Zahlung läuft asynchron (SEPA-Lastschrift /
          // Klarna) — das ist KEIN Fehler, Bestätigung folgt per E-Mail.
          setState({ status: "processing", payment: data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment: data });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", payment: data });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "error", error: "Statusprüfung fehlgeschlagen." });
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24">
        <div className="rounded-3xl bg-white border border-cream-300 shadow-soft p-10 text-center">
          {state.status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-sand-700 animate-spin" />
              <h1 className="mt-6 font-heading text-3xl md:text-4xl font-semibold text-sand-800">Zahlung wird geprüft …</h1>
              <p className="mt-3 text-sand-600">Einen Moment bitte. Das dauert in der Regel ein paar Sekunden.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-sage/20 text-sage-dark mb-2">
                <CheckCircle2 className="h-8 w-8" strokeWidth={1.8} />
              </div>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-sand-800">Buchung bestätigt 🎉</h1>
              <p className="mt-3 text-sand-600 leading-relaxed">
                Vielen Dank für deine Buchung! Du erhältst gleich eine Bestätigung per E-Mail mit allen Zugangsdaten zu deinem Kurs.
                Wir freuen uns auf dich am nächsten Montag.
              </p>
              {state.payment?.metadata?.course_title && (
                <div className="mt-6 inline-block rounded-2xl bg-cream-100 border border-cream-300 px-5 py-3 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-sand-500 font-semibold">Dein Kurs</div>
                  <div className="font-heading text-lg text-sand-800 font-semibold">{state.payment.metadata.course_title}</div>
                  <div className="text-sm text-sand-600 mt-1">
                    {(state.payment.amount_total / 100).toLocaleString("de-DE", { style: "currency", currency: (state.payment.currency || "eur").toUpperCase() })}
                  </div>
                </div>
              )}
              <Button asChild className="mt-8 rounded-full bg-sand-800 text-cream-50 hover:bg-sand-700 h-12 px-7">
                <Link to="/">Zurück zur Startseite <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Link>
              </Button>
            </>
          )}
          {state.status === "processing" && (
            <>
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-sage/20 text-sage-dark mb-2" data-testid="booking-processing-icon">
                <Clock className="h-8 w-8" strokeWidth={1.8} />
              </div>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-sand-800">Fast geschafft — deine Zahlung ist unterwegs</h1>
              <p className="mt-3 text-sand-600 leading-relaxed" data-testid="booking-processing-text">
                Deine Buchung ist bei uns eingegangen. Bei Zahlung per <strong>SEPA-Lastschrift</strong> oder <strong>Klarna</strong> dauert
                die Bestätigung durch die Bank <strong>1–3 Werktage</strong> — das ist ganz normal. Du musst nichts weiter tun:
                Sobald die Zahlung bestätigt ist, bekommst du automatisch deine Buchungsbestätigung mit allen Kurs-Zugangsdaten per E-Mail.
              </p>
              {state.payment?.metadata?.course_title && (
                <div className="mt-6 inline-block rounded-2xl bg-cream-100 border border-cream-300 px-5 py-3 text-left">
                  <div className="text-[10px] uppercase tracking-widest text-sand-500 font-semibold">Deine Buchung</div>
                  <div className="font-heading text-lg text-sand-800 font-semibold">{state.payment.metadata.course_title}</div>
                </div>
              )}
              <Button asChild className="mt-8 rounded-full bg-sand-800 text-cream-50 hover:bg-sand-700 h-12 px-7">
                <Link to="/">Zurück zur Startseite <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></Link>
              </Button>
            </>
          )}
          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <>
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-terra/15 text-terra mb-2">
                <AlertCircle className="h-8 w-8" strokeWidth={1.8} />
              </div>
              <h1 className="mt-4 font-heading text-3xl font-semibold text-sand-800">Status konnte nicht final geprüft werden</h1>
              <p className="mt-3 text-sand-600">
                Das passiert manchmal, wenn die Zahlung etwas länger braucht. Bitte prüfe deine E-Mails — sollte nichts ankommen, melde dich kurz bei uns.
              </p>
              <Button asChild className="mt-8 rounded-full bg-sand-800 text-cream-50 hover:bg-sand-700 h-12 px-7">
                <Link to="/#kontakt">Beratung kontaktieren</Link>
              </Button>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
